import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { XmppContext } from "@/stream/xmppClient"
import useCarRouteConfig from "@/hooks/useCarRouteConfig"
import { AvoidType, LoadType } from "@/api/adHocCommands"
import {
  availableAvoidOptions,
  availableLoadTypes,
  getAvailableAvoidOptions,
} from "@/components/maps/carRouteOptions"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { upsertRoute2gToll } from "@/reducers/routes2gSlice"
import { selectRoutingProvider } from "@/reducers/serviceConfigSelectors"
import i18n from "@/lib/i18n/i18n"

const sameValues = (valuesA: string[], valuesB: string[]) => {
  if (valuesA.length !== valuesB.length) return false
  const setA = new Set(valuesA)
  return valuesB.every((value) => setA.has(value))
}

export const useRouteOptions = (driverJid: string) => {
  const dispatch = useAppDispatch()
  const { client } = useContext(XmppContext)
  const routingProvider = useAppSelector(selectRoutingProvider)
  const { carRouteConfig, updateRouteConfig, refreshCarRouteConfig } =
    useCarRouteConfig(client, driverJid)

  const avoidOptions = useMemo(
    () => getAvailableAvoidOptions(availableAvoidOptions, routingProvider),
    [routingProvider],
  )

  const [selectedAvoid, setSelectedAvoid] = useState<AvoidType[]>([])
  const [selectedLoadTypes, setSelectedLoadTypes] = useState<LoadType[]>([])
  const [pendingAvoid, setPendingAvoid] = useState<AvoidType[]>([])
  const [pendingLoadTypes, setPendingLoadTypes] = useState<LoadType[]>([])
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    if (isUpdating) return
    setSelectedAvoid(
      avoidOptions
        .filter((option) => carRouteConfig?.avoid?.includes(option.value))
        .map((option) => option.value),
    )
    setPendingAvoid(carRouteConfig?.avoid ?? [])
    setSelectedLoadTypes(
      availableLoadTypes
        .filter((option) => carRouteConfig?.loadType?.includes(option.value))
        .map((option) => option.value),
    )
    setPendingLoadTypes(carRouteConfig?.loadType ?? [])
  }, [carRouteConfig, isUpdating, avoidOptions])

  const save = useCallback(
    async (avoid: AvoidType[], loadType: LoadType[]) => {
      setIsUpdating(true)
      try {
        await updateRouteConfig({ avoid, loadType })
        await refreshCarRouteConfig()
        dispatch(upsertRoute2gToll({ jid: driverJid, tolls: undefined }))
      } catch (error) {
        console.error("Failed to update route options:", error)
        const toaster = window.mapToaster ?? window.appToaster
        toaster?.show({
          message: i18n.t("maps.useRouteOptions.updateFailed"),
          intent: "danger",
          timeout: 3000,
        })
      } finally {
        setIsUpdating(false)
      }
    },
    [updateRouteConfig, refreshCarRouteConfig, dispatch, driverJid],
  )

  const applyAvoid = useCallback(
    async (selected: AvoidType[]) => {
      if (sameValues(selected, selectedAvoid)) return
      setSelectedAvoid(
        avoidOptions
          .filter((option) => selected.includes(option.value))
          .map((option) => option.value),
      )
      await save(selected, selectedLoadTypes)
    },
    [selectedAvoid, selectedLoadTypes, avoidOptions, save],
  )

  const applyLoadTypes = useCallback(
    async (selected: LoadType[]) => {
      if (sameValues(selected, selectedLoadTypes)) return
      setSelectedLoadTypes(
        availableLoadTypes
          .filter((option) => selected.includes(option.value))
          .map((option) => option.value),
      )
      await save(selectedAvoid, selected)
    },
    [selectedAvoid, selectedLoadTypes, save],
  )

  return {
    routingProvider,
    avoidOptions,
    loadTypeOptions: availableLoadTypes,
    selectedAvoid,
    selectedLoadTypes,
    pendingAvoid,
    pendingLoadTypes,
    setPendingAvoid,
    setPendingLoadTypes,
    applyAvoid,
    applyLoadTypes,
    isUpdating,
  }
}
